import React from 'react';

export type TierKey = 'trainee' | 'junior' | 'middle' | 'senior';

type Props = {
  tier: TierKey;
  intensity?: 'subtle' | 'bold';
  className?: string;
};

/**
 * A decorative background for the avatar stage, tuned per tier.
 * Renders:
 * - faint grid
 * - two radial blobs (tier-colored)
 * - a soft vignette
 * - a subtle ring
 *
 * Place inside a relatively positioned container and keep it behind media:
 * <div className="relative">
 *   <AvatarTierBackground tier="senior" />
 *   ...avatar media...
 * </div>
 */
export const AvatarTierBackground: React.FC<Props> = ({ tier, intensity = 'subtle', className }) => {
  // Opacities and sizes adjust with intensity
  const gridOpacity = intensity === 'bold' ? 0.10 : 0.06;
  const blobOpacity = intensity === 'bold' ? 0.30 : 0.20;
  const vignetteOpacity = intensity === 'bold' ? 0.20 : 0.12;
  const ringOpacity = intensity === 'bold' ? 0.22 : 0.14;

  const palette = getPalette(tier);

  return (
    <div
      aria-hidden="true"
      className={[
        'absolute inset-0 pointer-events-none overflow-hidden rounded-xl',
        className || '',
      ].join(' ')}
    >
      {/* Grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, rgba(15,23,42,${gridOpacity}) 0px, rgba(15,23,42,${gridOpacity}) 1px, transparent 1px, transparent 36px),
            repeating-linear-gradient(90deg, rgba(15,23,42,${gridOpacity}) 0px, rgba(15,23,42,${gridOpacity}) 1px, transparent 1px, transparent 36px)
          `,
          backgroundSize: 'auto',
        }}
      />

      {/* Blobs */}
      <div
        className="absolute -top-10 -left-10 w-[260px] h-[260px] rounded-full"
        style={{
          background: `radial-gradient(closest-side, ${withAlpha(palette.primary, blobOpacity)}, rgba(0,0,0,0) 70%)`,
          filter: 'blur(10px)',
        }}
      />
      <div
        className="absolute -bottom-10 -right-10 w-[260px] h-[260px] rounded-full"
        style={{
          background: `radial-gradient(closest-side, ${withAlpha(palette.secondary, blobOpacity)}, rgba(0,0,0,0) 70%)`,
          filter: 'blur(10px)',
        }}
      />

      {/* Premium center glow for Senior */}
      {tier === 'senior' && (
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(closest-side, ${withAlpha('#fff4cc', 0.18)}, rgba(255,255,255,0) 65%)`,
            mixBlendMode: 'soft-light',
          }}
        />
      )}

      {/* Soft vignette */}
      <div
        className="absolute inset-0 rounded-xl"
        style={{
          boxShadow: `inset 0 0 120px ${withAlpha('#000000', vignetteOpacity)}`,
        }}
      />

      {/* Outer ring */}
      <div
        className="absolute"
        style={{
          left: '-28px',
          top: '-28px',
          width: 'calc(100% + 56px)',
          height: 'calc(100% + 56px)',
          borderRadius: '9999px',
          border: `2px solid ${withAlpha('#000000', ringOpacity)}`,
        }}
      />
    </div>
  );
};

function getPalette(tier: TierKey) {
  switch (tier) {
    case 'senior':
      return { primary: '#8B5CF6', secondary: '#06B6D4' }; // violet + cyan
    case 'middle':
      return { primary: '#0EA5E9', secondary: '#06B6D4' }; // sky + cyan
    case 'junior':
      return { primary: '#10B981', secondary: '#22C55E' }; // emerald + green
    case 'trainee':
    default:
      return { primary: '#64748B', secondary: '#60A5FA' }; // slate + sky
  }
}

function withAlpha(hex: string, alpha: number) {
  // Accept #RRGGBB
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default AvatarTierBackground;
